import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  BRAND,
  FIGURE_HEIGHT,
  SLOUCHED,
  SPLASH_FIGURE_FRACTION,
  STROKE,
  UPRIGHT,
  blend,
  shape,
} from '@/brand/figure.js';
import { useAuth } from '@/stores/auth.js';
import { isNative } from '@/lib/device.js';
import { warmAfterLaunch, warmFirstScreen } from '@/lib/warmup.js';

/**
 * Launch splash: the figure starts slumped — head forward, back rounded —
 * straightens into the app mark, rests upright for a beat, and the app opens.
 *
 *  1. The first frame matches the native launch image exactly (same figure
 *     source, size and position), so hiding the native splash moves nothing.
 *  2. The figure straightens straight away. Meanwhile auth resolves and
 *     Progress's history loads — network work, it does not touch the motion.
 *  3. Upright, it holds briefly (longer only if the history is still on its
 *     way, capped), then fades into a filled Progress.
 *
 * The pose model is deliberately not prepared here: initialising it holds the
 * main thread for seconds, and every place inside the splash showed as a
 * stall (a frozen figure before or after the motion). It warms up once the
 * app is open and idle (`warmAfterLaunch`); opening a session before then
 * shows the camera at once and the checks start when the model is ready.
 */
const HOLD_MS = 150;
const STRAIGHTEN_MS = 1100;
/** The beat upright before the app opens. */
const UPRIGHT_REST_MS = 450;
/** If Progress's history is still loading, wait at most this long upright. */
const DATA_CAP_MS = 1200;
const FADE_MS = 280;

const ease = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `rgb(${pa.map((v, i) => Math.round(v + (pb[i]! - v) * t)).join(',')})`;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));
const nextPaint = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

/** Resolves once auth knows who (if anyone) is signed in. */
function authSettled(): Promise<void> {
  if (!useAuth.getState().loading) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = useAuth.subscribe((state) => {
      if (!state.loading) {
        unsubscribe();
        resolve();
      }
    });
  });
}

async function hideNativeSplash(): Promise<void> {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 0 });
  } catch {
    // Nothing to hide.
  }
}

export function Splash({ onDone }: { onDone(): void }): ReactNode {
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [t, setT] = useState(reduceMotion ? 1 : 0);
  const [leaving, setLeaving] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    let cancelled = false;

    const straighten = (): Promise<void> =>
      new Promise<void>((resolve) => {
        if (reduceMotion) return resolve();
        const start = performance.now() + HOLD_MS;
        const frame = (now: number): void => {
          if (cancelled) return resolve();
          const p = Math.min(1, Math.max(0, (now - start) / STRAIGHTEN_MS));
          setT(ease(p));
          if (p < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });

    // Auth, then the first screen's data, alongside the animation.
    const firstScreen = (async () => {
      await authSettled();
      const uid = useAuth.getState().user?.uid;
      if (uid) await warmFirstScreen(uid);
    })();

    void (async () => {
      await nextPaint();
      await hideNativeSplash();
      await straighten();
      if (cancelled) return;
      const upright = performance.now();
      await Promise.race([firstScreen, sleep(DATA_CAP_MS)]);
      const rested = performance.now() - upright;
      if (rested < UPRIGHT_REST_MS) await sleep(UPRIGHT_REST_MS - rested);
      if (cancelled) return;

      setLeaving(true);
      await sleep(FADE_MS);
      if (cancelled) return;
      onDoneRef.current();
      warmAfterLaunch();
    })();

    return () => {
      cancelled = true;
    };
  }, [reduceMotion]);

  const pose = blend(SLOUCHED, UPRIGHT, t);
  const { d, head } = shape(pose);
  const color = mix(BRAND.slouched, BRAND.upright, Math.max(0, (t - 0.45) / 0.55));
  // The 512 frame scaled so the figure is SPLASH_FIGURE_FRACTION of the screen
  // height, centred — the same placement as the native launch image.
  const frame = `${((512 / FIGURE_HEIGHT) * SPLASH_FIGURE_FRACTION * 100).toFixed(3)}dvh`;

  return (
    <div
      aria-hidden
      data-splash
      className="fixed inset-0 z-[100] grid place-items-center"
      style={{
        backgroundColor: BRAND.ground,
        opacity: leaving ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <svg viewBox="0 0 512 512" style={{ width: frame, height: frame }}>
        <g fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
          <path d={d} />
          <circle cx={head.cx} cy={head.cy} r={head.r} />
        </g>
      </svg>
    </div>
  );
}
